<template lang="pug">
div
  .ui.segment.form.new_folder
    .ui.grid.stackable
      .three.wide.column.mobile.hidden.text-center
        h3(is="sui-header" icon)
          sui-icon(:name="icon" size="big")
          sui-header-content Ordner

      .thirteen.wide.column.hidden
        sui-form-field(required)
          LabelWithHint(tooltip-key="folders.edit.name.tooltip") Name
          sui-input(type="text" placeholder="Dateiname" v-model="name" name="name")
          sui-label.hovering(v-if="isNameMissing" pointing color="red") Name ist erforderlich

        sui-form-fields(fields="two")
          sui-form-field
            LabelWithHint(tooltip-key="folders.edit.series.tooltip") Serie
            sui-checkbox(label="Serie" toggle v-model="series")

          sui-form-field(inline)
            label(class="select") Ordnersortierung
            SuiDropdown(
              :options="ORDER_OPTIONS"
              v-model="ordering"
              multiple
              fluid
              selection
            )

        sui-form-field
          LabelWithHint Position des Wurzelordners in Sortierung
          sui-input(type="number" v-model="order" name="order")

        sui-form-field
          LabelWithHint(tooltip-key="folders.edit.description.tooltip") Beschreibung
          textarea(rows=3 placeholder="Beschreibung" v-model="description" name="description")

        h2 Berechtigungen

        InodeAclInput(:inode="folder" @input="setAcl")

    .ui.message.visible.error(v-if="driveError") Ordner konnte nicht gespeichert werden ({{ driveError.response.status }}).
    .error(v-if="isAnyError") Das Formular ist nicht korrekt ausgefüllt.
    .ui.button.primary(@click="saveForm" :class="{disabled: isAnyError}") Speichern
    .ui.button(@click="$router.go(-1)") Abbrechen
</template>

<script lang="ts">
import InodeAclInput from "./InodeAclInput.vue"
import RemoteDropdown from "../RemoteDropdown.vue"
import ListItem from "../ListItem.vue"
import LabelWithHint from "../LabelWithHint.vue"
import { computed, PropType, ref, watch } from "vue"
import { Inode, InodeOrder, UpdateFolderRequestInode } from "suus-api"
import { DriveVuexKiller } from "app/vuex-killers"
import { useRouter } from "app/vue-instance/use-router"

type FormErrors = {
  name?: "required"
  unexpected?: string
}

type OrderOption = { value: InodeOrder; text: string }

const ORDER_OPTIONS: OrderOption[] = [
  { value: "<name", text: "Name (A-Z)" },
  { value: ">name", text: "Name (Z-A)" },
  { value: "<created_at", text: "Hochgeladen (Alt -> Neu)" },
  { value: ">created_at", text: "Hochgeladen (Neu -> Alt)" },
  { value: "<physically_created_at", text: "Datierung (Alt -> Neu)" },
  { value: ">physically_created_at", text: "Datierung (Neu -> Alt)" },
]

export default {
  name: "FolderForm",
  components: {
    LabelWithHint,
    InodeAclInput,
    RemoteDropdown,
    ListItem,
  },
  props: {
    folder: { type: Object as PropType<Inode>, required: true },
  },
  setup(props) {
    const createFolder = DriveVuexKiller.createFolder
    const updateInode = DriveVuexKiller.updateInode
    const driveError = DriveVuexKiller.driveError
    const setDriveError = DriveVuexKiller.setDriveError
    const setDriveSuccess = DriveVuexKiller.setDriveSuccess

    const formErrors = ref<FormErrors>({})
    const isAnyError = computed(() =>
      Object.values(formErrors.value).some((v) => !!v)
    )
    const validateForm = () => {
      formErrors.value = {}
      if (!name.value) {
        formErrors.value.name = "required"
      }
      return !Object.values(formErrors.value).some((v) => !!v)
    }

    const name = ref(props.folder.name)
    const isNameMissing = computed(() => formErrors.value.name === "required")
    watch(name, () => {
      if (isNameMissing.value && name.value) {
        formErrors.value = {
          ...formErrors.value,
          name: undefined,
        }
      }
    })

    const description = ref(props.folder.description)
    const ordering = ref<InodeOrder[]>(props.folder.ordering)
    // Order of a root folder in the menu
    const order = ref(props.folder.order)

    const series = ref(props.folder.series)
    const type = computed(() => (series.value ? "Container" : "Folder"))
    const icon = computed(() =>
      series.value ? "suus-single-copies" : "suus-folder-13"
    )

    const acl = ref({
      parent_id: props.folder.parent_id,
      restrict_to_id: props.folder.restrict_to_id,
      owner_id: props.folder.owner_id,
      owner_type: props.folder.owner_type,
    })
    const setAcl = (value) => (acl.value = value)

    const formData = computed(() => {
      const inode: UpdateFolderRequestInode = {
        name: name.value,
        ordering: ordering.value,
        order: order.value,
        type: type.value,
        description: description.value,
        ...acl.value,
      }
      return { inode }
    })

    const router = useRouter()

    const saveForm = async () => {
      if (!validateForm()) return
      try {
        let id = props.folder?.id?.toString()
        if (id) {
          await updateInode(props.folder.update_url, formData.value)
        } else {
          const response = await createFolder(formData.value)
          id = response.id.toString() // set id to newly created id
        }
        setDriveSuccess("Erfolgreich gespeichert")
        router.push({ name: "drive/folder", params: { inode_id: id } })
      } catch (e) {
        console.log(e)
        setDriveError(e)
      }
    }

    return {
      ORDER_OPTIONS,
      createFolder,
      updateInode,
      driveError,
      setDriveError,
      setDriveSuccess,
      isAnyError,
      validateForm,
      name,
      isNameMissing,
      description,
      ordering,
      order,
      series,
      type,
      icon,
      setAcl,
      formData,
      saveForm,
    }
  },
}
</script>

<style scoped lang="sass">
.new_folder
  .item .header .field
    clear: both
    margin: 2px 2px 0 !important

.upload-fields
  display: flex
  .field
    padding-right: 1rem
</style>
